import { message } from './message'

export interface FileRules {
  /** Extensions with the dot, comma-separated: `.pdf` or `.pdf,.jpg`. */
  accept: string
  maxSizeMb: number
}

const acceptedExtensions = (accept: string) =>
  accept.split(',').map((extension) => extension.trim().toLowerCase())

const acceptedFormats = (accept: string) =>
  acceptedExtensions(accept).map((extension) => extension.replace(/^\./, ''))

/** The `Hint` under an upload control: `pdf, jpg files with a size less than 5MB`. */
export const fileRulesTip = ({ accept, maxSizeMb }: FileRules) =>
  `${acceptedFormats(accept).join(', ')} files with a size less than ${maxSizeMb}MB`

/**
 * Whether a picked file is one the form will take — call it in an upload's `before-upload`
 * and let the `false` stop the upload. **It toasts the reason itself**, so a caller adds no
 * message of its own.
 *
 * Checked by extension and not by MIME type: a PDF from some systems does not carry
 * one reliably.
 */
export function isAcceptedFile(file: File, rules: FileRules): boolean {
  const name = file.name.toLowerCase()

  if (!acceptedExtensions(rules.accept).some((ext) => name.endsWith(ext))) {
    const formats = acceptedFormats(rules.accept)
      .map((format) => format.toUpperCase())
      .join(' or ')

    message.error(`File must be ${formats} format`)
    return false
  }

  if (file.size > rules.maxSizeMb * 1024 * 1024) {
    message.error(`File size must be less than ${rules.maxSizeMb}MB`)
    return false
  }

  return true
}
